import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  Header,
  SpaceBetween,
  StatusIndicator,
  Tabs,
} from '@cloudscape-design/components';
import InventoryTxTable from 'features/inventory/components/InventoryTxTable';
import useInventoryTxQuery from 'features/inventory/hooks/useInventoryTxQuery';
import useRealtimeInventoryByProductVersion from 'features/inventory/hooks/useRealtimeInventoryByProductVersion';
import useTravelersByPPartQuery from 'features/inventory/hooks/useTravelersByPPartQuery';
import ProductDisplay from 'features/products/components/ProductDisplay';
import useGetProductById from 'features/products/hooks/useGetProductById';
import useGetProductVersion from 'features/products/hooks/useGetProductVersion';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import ScanTravelerInventoryModal from './components/ScanTravelerInventoryModal';
import TravelersTable from './components/TravelersTable';

export default function InventoryDetailPage() {
  const { productDocId, versionId } = useParams();
  const navigate = useNavigate();
  const [scanOpen, setScanOpen] = useState(false);

  const {
    data: product,
    isLoading: productLoading,
    error: productError,
  } = useGetProductById(productDocId);

  const {
    data: version,
    isLoading: versionLoading,
  } = useGetProductVersion(productDocId, versionId);

  const {
    data: inventory,
    status: inventoryStatus,
  } = useRealtimeInventoryByProductVersion(productDocId, versionId);

  const {
    pageData: transactions = [],
    isFetchingNextPage: txLoading,
    refetch: refetchTx,
    CustomPagination: TxPagination,
  } = useInventoryTxQuery(productDocId, versionId);

  const project = version?.projectId ?? null;
  const pPart = version?.pPartId ?? null;

  const {
    pageData: availableTravelers = [],
    isFetchingNextPage: availableLoading,
    refetch: refetchAvailable,
    CustomPagination: AvailablePagination,
  } = useTravelersByPPartQuery(project, pPart, false);

  const {
    pageData: shippedTravelers = [],
    isFetchingNextPage: shippedLoading,
    refetch: refetchShipped,
    CustomPagination: ShippedPagination,
  } = useTravelersByPPartQuery(project, pPart, true);

  if (productLoading || inventoryStatus === 'loading' || versionLoading) {
    return <StatusIndicator type="loading">Loading...</StatusIndicator>;
  }

  if (productError) {
    return <Alert type="error">{productError.message}</Alert>;
  }

  if (!product || !version) {
    return <Alert type="error">Product or version not found.</Alert>;
  }

  const onHand = inventory?.onHand ?? 0;
  const incomingSupply = inventory?.incomingSupply ?? 0;
  const committedDemand = inventory?.committedDemand ?? 0;
  const netPosition = inventory?.netPosition ?? (onHand + incomingSupply - committedDemand);
  const reorderPolicy = version?.reorderPolicy;

  const handleSuccess = async () => {
    await Promise.all([
      refetchTx(),
      refetchAvailable(),
      refetchShipped(),
    ]);
  };

  return (
    <SpaceBetween size="l">
      <Container
        header={(
          <Header
            variant="awsui-h1-sticky"
            description={[project, pPart].filter(Boolean).join('-') || '-'}
            actions={(
              <SpaceBetween direction="horizontal" size="xs">
                <Button onClick={() => setScanOpen(true)}>
                  Scan traveler
                </Button>
                <Button
                  onClick={() => navigate(`/inventory/${productDocId}/${versionId}/reconcile`)}
                >
                  Reconcile inventory
                </Button>
              </SpaceBetween>
            )}
          >
            <ProductDisplay productDocId={productDocId} fields={['sku', 'name']} />
            {' '}
            · Version
            {' '}
            {versionId}
          </Header>
        )}
      >
        <ColumnLayout columns={4} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">On Hand</Box>
            <Box fontSize="heading-xl">{onHand}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Incoming Supply</Box>
            <Box fontSize="heading-xl">{incomingSupply}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Committed Demand</Box>
            <Box fontSize="heading-xl">{committedDemand}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Net Position</Box>
            <Box fontSize="heading-xl">{netPosition}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Reorder Policy</Box>
            <Box>{reorderPolicy?.enabled ? 'Enabled' : 'Off'}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Trigger Quantity</Box>
            <Box>{reorderPolicy?.triggerQuantity ?? '-'}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Target Level</Box>
            <Box>{reorderPolicy?.targetLevel ?? '-'}</Box>
          </div>
        </ColumnLayout>
      </Container>

      <InventoryTxTable
        items={transactions}
        loading={txLoading}
        pagination={<TxPagination />}
      />

      <Tabs
        tabs={[
          {
            label: 'Available Travelers',
            id: 'available',
            content: (
              <TravelersTable
                items={availableTravelers}
                loading={availableLoading}
                pagination={<AvailablePagination />}
              />
            ),
          },
          {
            label: 'Shipped Travelers',
            id: 'shipped',
            content: (
              <TravelersTable
                items={shippedTravelers}
                loading={shippedLoading}
                pagination={<ShippedPagination />}
              />
            ),
          },
        ]}
        variant="container"
      />

      {scanOpen && (
        <ScanTravelerInventoryModal
          open
          onClose={() => setScanOpen(false)}
          onSuccess={handleSuccess}
        />
      )}
    </SpaceBetween>
  );
}
